import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
// TASK-66 PR-D: fetchTeamsForUser unions teams.player1/2 ∪
// team_members.user_id (was UPPDRAG-62 PR-D's three-source union including
// captain_id; captain_id stripped — captain users still resolved via the
// team_members branch).
import { fetchTeamsForUser } from "@/lib/teamMembershipQueries";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy } from "lucide-react";
import { cn } from "@/lib/utils";

const statusLabels: Record<string, string> = {
  draft: "Utkast",
  registration_open: "Anmälan",
  active: "Pågår",
  completed: "Avslutad",
  archived: "Arkiverad",
};

const statusVariant: Record<string, "default" | "secondary" | "outline"> = {
  active: "default",
  registration_open: "secondary",
  completed: "outline",
};

interface LeagueListPanelProps {
  selectedLeagueId: string | null;
  onSelectLeague: (id: string) => void;
}

export default function LeagueListPanel({
  selectedLeagueId,
  onSelectLeague,
}: LeagueListPanelProps) {
  const { user } = useAuth();

  // Fetch all leagues the user could be interested in
  const { data: leagues = [], isLoading } = useQuery({
    queryKey: ["league-list-panel"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("id, name, category, status, start_date, end_date")
        .in("status", ["active", "registration_open", "completed"])
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // TASK-66 PR-D: shared fetchTeamsForUser util unions teams.player1/2 +
  // team_members.user_id, deduped. Captain users resolved via the
  // team_members branch.
  const { data: myTeamLeagueIds = [] } = useQuery({
    queryKey: ["my-league-ids", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const teams = await fetchTeamsForUser<{ id: string; league_id: string }>(
        supabase,
        user.id,
        { columns: "id, league_id", status: "active" },
      );
      return teams.map((t) => t.league_id);
    },
    enabled: !!user?.id,
  });

  const myLeagueIdSet = new Set(myTeamLeagueIds);

  return (
    <div className="p-3 space-y-1">
      <h2 className="text-sm font-semibold px-1 pb-2 flex items-center gap-2">
        <Trophy className="h-4 w-4" /> Serier
      </h2>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      ) : leagues.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-6">
          Inga serier
        </p>
      ) : (
        leagues.map((league) => (
          <button
            key={league.id}
            onClick={() => onSelectLeague(league.id)}
            className={cn(
              "w-full text-left rounded-lg p-3 transition-colors",
              selectedLeagueId === league.id
                ? "bg-primary/10 border border-primary/30"
                : "hover:bg-muted/50"
            )}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium truncate">{league.name}</p>
              {myLeagueIdSet.has(league.id) && (
                <Badge variant="secondary" className="text-[10px] shrink-0 ml-1">
                  Min
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge
                variant={statusVariant[league.status] || "outline"}
                className="text-[10px]"
              >
                {statusLabels[league.status] || league.status}
              </Badge>
              <span className="text-[10px] text-muted-foreground">
                {league.category}
              </span>
            </div>
          </button>
        ))
      )}
    </div>
  );
}
